import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../components/ui/tooltip';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import {
  ChevronLeft,
  ChevronRight,
  Calendar,
  ZoomIn,
  ZoomOut,
  Loader2,
  FolderKanban,
  CheckSquare,
  ChevronDown,
  User,
  Clock,
  Flag,
  X
} from 'lucide-react';
import { 
  format, 
  parseISO, 
  startOfMonth, 
  endOfMonth, 
  eachDayOfInterval, 
  eachWeekOfInterval,
  addMonths, 
  subMonths,
  differenceInDays,
  isWithinInterval,
  startOfWeek,
  endOfWeek,
  isSameMonth,
  isToday,
  addDays
} from 'date-fns';

const STATUS_COLORS = {
  planning: { bg: 'bg-slate-200', text: 'text-slate-700', bar: '#94a3b8' },
  active: { bg: 'bg-blue-100', text: 'text-blue-700', bar: '#3b82f6' },
  on_hold: { bg: 'bg-amber-100', text: 'text-amber-700', bar: '#f59e0b' },
  completed: { bg: 'bg-emerald-100', text: 'text-emerald-700', bar: '#10b981' },
  backlog: { bg: 'bg-slate-100', text: 'text-slate-600', bar: '#cbd5e1' },
  in_progress: { bg: 'bg-blue-100', text: 'text-blue-700', bar: '#3b82f6' },
  in_review: { bg: 'bg-purple-100', text: 'text-purple-700', bar: '#8b5cf6' },
  blocked: { bg: 'bg-red-100', text: 'text-red-700', bar: '#ef4444' }
};

const TASK_PRIORITY_COLORS = {
  critical: '#ef4444',
  high: '#f97316',
  medium: '#3b82f6',
  low: '#6b7280'
};

const TASK_STATUS_CONFIG = {
  backlog: { label: 'Backlog', color: 'bg-slate-100 text-slate-600' },
  in_progress: { label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  in_review: { label: 'In Review', color: 'bg-purple-100 text-purple-700' },
  completed: { label: 'Completed', color: 'bg-emerald-100 text-emerald-700' },
  blocked: { label: 'Blocked', color: 'bg-red-100 text-red-700' }
};

// Task Detail Modal Component
const TaskDetailModal = ({ task, users, projects, open, onClose }) => {
  if (!task) return null;
  
  const assignee = users?.find(u => u.id === task.assignee_id);
  const project = projects?.find(p => p.id === task.project_id);
  const statusConfig = TASK_STATUS_CONFIG[task.status] || TASK_STATUS_CONFIG.backlog;
  const priorityColor = TASK_PRIORITY_COLORS[task.priority] || TASK_PRIORITY_COLORS.medium;
  
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md" data-testid="task-detail-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <CheckSquare className="w-5 h-5 text-slate-400" />
            Task Details
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Task Name */}
          <div>
            <h3 className="font-semibold text-slate-900 text-base" data-testid="task-detail-name">
              {task.name}
            </h3>
            {task.description && (
              <p className="text-sm text-slate-500 mt-1">{task.description}</p>
            )}
          </div>
          
          {/* Status & Priority Badges */}
          <div className="flex items-center gap-2 flex-wrap">
            <Badge className={statusConfig.color} data-testid="task-detail-status">
              {statusConfig.label}
            </Badge>
            <Badge 
              style={{ backgroundColor: `${priorityColor}20`, color: priorityColor }}
              data-testid="task-detail-priority"
            >
              <Flag className="w-3 h-3 mr-1" />
              {task.priority?.charAt(0).toUpperCase() + task.priority?.slice(1)}
            </Badge>
          </div>
          
          {/* Task Info Grid */}
          <div className="grid grid-cols-2 gap-4 pt-2 border-t border-slate-100">
            {/* Project */}
            {project && (
              <div className="flex items-start gap-2">
                <FolderKanban className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Project</p>
                  <p className="text-sm font-medium text-slate-700" data-testid="task-detail-project">
                    {project.name}
                  </p>
                </div>
              </div>
            )}
            
            {/* Assignee */}
            <div className="flex items-start gap-2">
              <User className="w-4 h-4 text-slate-400 mt-0.5" />
              <div>
                <p className="text-xs text-slate-500">Assignee</p>
                <p className="text-sm font-medium text-slate-700" data-testid="task-detail-assignee">
                  {assignee ? assignee.name : 'Unassigned'}
                </p>
              </div>
            </div>
            
            {/* Start Date */}
            {task.start_date && (
              <div className="flex items-start gap-2">
                <Calendar className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Start Date</p>
                  <p className="text-sm font-medium text-slate-700">
                    {format(parseISO(task.start_date), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>
            )}
            
            {/* Due Date */}
            {task.due_date && (
              <div className="flex items-start gap-2">
                <Clock className="w-4 h-4 text-slate-400 mt-0.5" />
                <div>
                  <p className="text-xs text-slate-500">Due Date</p>
                  <p className="text-sm font-medium text-slate-700" data-testid="task-detail-due-date">
                    {format(parseISO(task.due_date), 'MMM d, yyyy')}
                  </p>
                </div>
              </div>
            )}
          </div>
          
          {/* Close Button */}
          <div className="pt-4 border-t border-slate-100">
            <Button 
              variant="outline" 
              onClick={() => onClose(false)} 
              className="w-full"
              data-testid="task-detail-close-btn"
            >
              Close
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Gantt Bar Component
const GanttBar = ({ item, startDate, endDate, dayWidth, totalDays, isTask = false, onClick }) => {
  const itemStart = item.start_date ? parseISO(item.start_date) : null;
  const itemEnd = isTask ? (item.due_date ? parseISO(item.due_date) : null) : (item.end_date ? parseISO(item.end_date) : null);
  
  if (!itemStart && !itemEnd) return null;
  
  // Calculate position
  const effectiveStart = itemStart || itemEnd;
  const effectiveEnd = itemEnd || itemStart;
  
  const startOffset = Math.max(0, differenceInDays(effectiveStart, startDate));
  const duration = Math.max(1, differenceInDays(effectiveEnd, effectiveStart) + 1);
  
  // Don't render if completely outside visible range
  if (startOffset > totalDays || startOffset + duration < 0) return null;
  
  const left = startOffset * dayWidth;
  const width = Math.min(duration * dayWidth, (totalDays - startOffset) * dayWidth);
  
  const statusColor = STATUS_COLORS[item.status] || STATUS_COLORS.planning;
  const barColor = isTask && item.priority ? TASK_PRIORITY_COLORS[item.priority] : statusColor.bar;
  
  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            className={`absolute h-6 rounded-md cursor-pointer transition-all duration-150 
              hover:brightness-110 hover:shadow-lg hover:scale-y-110 hover:z-20 
              active:scale-95 ${isTask ? 'top-1' : 'top-0.5'}`}
            style={{
              left: `${left}px`,
              width: `${Math.max(width, 20)}px`,
              backgroundColor: barColor,
              opacity: item.status === 'completed' ? 0.7 : 1,
              boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
            }}
            onClick={(e) => {
              e.stopPropagation();
              onClick?.(item, isTask);
            }}
            data-testid={`gantt-bar-${isTask ? 'task' : 'project'}-${item.id}`}
          >
            {width > 60 && (
              <span className="text-xs text-white font-medium px-2 truncate block leading-6">
                {item.name}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side="top" className="max-w-xs">
          <div className="space-y-1">
            <p className="font-semibold">{item.name}</p>
            <p className="text-xs text-slate-500">
              {itemStart && format(itemStart, 'MMM d')} - {itemEnd && format(itemEnd, 'MMM d, yyyy')}
            </p>
            <Badge className={`${statusColor.bg} ${statusColor.text} text-xs`}>
              {item.status.replace('_', ' ')}
            </Badge>
            {isTask && item.priority && (
              <Badge 
                className="ml-1 text-xs"
                style={{ backgroundColor: TASK_PRIORITY_COLORS[item.priority], color: 'white' }}
              >
                {item.priority}
              </Badge>
            )}
            <p className="text-xs text-blue-600 mt-1">Click to view details</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

// Project Row Component
const ProjectRow = ({ project, tasks, startDate, dayWidth, totalDays, expanded, onToggle, onProjectClick, onTaskClick }) => {
  const projectTasks = tasks.filter(t => t.project_id === project.id);
  
  return (
    <>
      {/* Project Row */}
      <div className="flex border-b border-slate-100 hover:bg-slate-50/50 transition-colors">
        {/* Project Name Cell */}
        <div className="w-64 min-w-64 flex-shrink-0 p-3 border-r border-slate-100 bg-white sticky left-0 z-10">
          <div className="flex items-center gap-2">
            <button 
              onClick={() => onToggle(project.id)}
              className="p-0.5 hover:bg-slate-100 rounded transition-colors"
              data-testid={`gantt-toggle-project-${project.id}`}
            >
              {expanded ? (
                <ChevronDown className="w-4 h-4 text-slate-400" />
              ) : (
                <ChevronRight className="w-4 h-4 text-slate-400" />
              )}
            </button>
            <FolderKanban className="w-4 h-4 text-blue-500 flex-shrink-0" />
            <span 
              className="font-medium text-sm text-slate-900 truncate cursor-pointer hover:text-blue-600 hover:underline"
              onClick={() => onProjectClick(project)}
              data-testid={`gantt-project-name-${project.id}`}
            >
              {project.name}
            </span>
          </div>
          <div className="ml-7 mt-1 flex items-center gap-2 text-xs text-slate-500">
            <Badge className={`${STATUS_COLORS[project.status]?.bg} ${STATUS_COLORS[project.status]?.text} text-xs`}>
              {project.status}
            </Badge>
            <span>{projectTasks.length} tasks</span>
          </div>
        </div>
        
        {/* Project Timeline */}
        <div className="flex-1 relative h-14" style={{ minWidth: `${totalDays * dayWidth}px` }}>
          <GanttBar
            item={project}
            startDate={startDate}
            endDate={addDays(startDate, totalDays)}
            dayWidth={dayWidth}
            totalDays={totalDays}
            onClick={(item) => onProjectClick(item)}
          />
        </div>
      </div>
      
      {/* Task Rows */}
      {expanded && projectTasks.map(task => (
        <div key={task.id} className="flex border-b border-slate-50 hover:bg-blue-50/30 transition-colors">
          {/* Task Name Cell */}
          <div className="w-64 min-w-64 flex-shrink-0 p-2 pl-10 border-r border-slate-100 bg-white sticky left-0 z-10">
            <div className="flex items-center gap-2">
              <CheckSquare className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" />
              <span 
                className="text-sm text-slate-700 truncate cursor-pointer hover:text-blue-600 hover:underline"
                onClick={() => onTaskClick(task)}
                data-testid={`gantt-task-name-${task.id}`}
              >
                {task.name}
              </span>
            </div>
            <div className="ml-5 mt-0.5">
              <Badge 
                className="text-xs"
                style={{ 
                  backgroundColor: `${TASK_PRIORITY_COLORS[task.priority]}20`,
                  color: TASK_PRIORITY_COLORS[task.priority]
                }}
              >
                {task.priority}
              </Badge>
            </div>
          </div>
          
          {/* Task Timeline */}
          <div className="flex-1 relative h-10" style={{ minWidth: `${totalDays * dayWidth}px` }}>
            <GanttBar
              item={task}
              startDate={startDate}
              endDate={addDays(startDate, totalDays)}
              dayWidth={dayWidth}
              totalDays={totalDays}
              isTask={true}
              onClick={(item, isTask) => onTaskClick(item)}
            />
          </div>
        </div>
      ))}
    </>
  );
};

export const GanttPage = () => {
  const { api } = useAuth();
  const { subscribe } = useWebSocket();
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [viewMode, setViewMode] = useState('month'); // 'month', 'quarter'
  const [expandedProjects, setExpandedProjects] = useState(new Set());
  const [statusFilter, setStatusFilter] = useState('all');
  const [dayWidth, setDayWidth] = useState(28);
  const scrollContainerRef = useRef(null);
  
  // Task detail modal state
  const [selectedTask, setSelectedTask] = useState(null);
  const [taskModalOpen, setTaskModalOpen] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [projectsRes, tasksRes, usersRes] = await Promise.all([
        api.get('/projects'),
        api.get('/tasks'),
        api.get('/users')
      ]);
      setProjects(projectsRes.data);
      setTasks(tasksRes.data);
      setUsers(usersRes.data);
      // Expand all projects by default
      setExpandedProjects(new Set(projectsRes.data.map(p => p.id)));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Real-time updates
  useEffect(() => {
    const unsubscribes = [];
    
    ['project', 'task'].forEach(entity => {
      ['created', 'updated', 'deleted'].forEach(action => {
        unsubscribes.push(subscribe(`${entity}_${action}`, () => {
          fetchData();
        }));
      });
    });

    return () => unsubscribes.forEach(unsub => unsub());
  }, [subscribe, fetchData]);

  // Calculate date range based on view mode
  const { startDate, endDate, days, totalDays } = useMemo(() => {
    let start, end;
    
    if (viewMode === 'month') {
      start = startOfMonth(currentDate);
      end = endOfMonth(currentDate);
    } else {
      // Quarter view - show 3 months
      start = startOfMonth(currentDate);
      end = endOfMonth(addMonths(currentDate, 2));
    }
    
    const daysArray = eachDayOfInterval({ start, end });
    return {
      startDate: start,
      endDate: end,
      days: daysArray,
      totalDays: daysArray.length
    };
  }, [currentDate, viewMode]);

  // Get weeks for header
  const weeks = useMemo(() => {
    return eachWeekOfInterval({ start: startDate, end: endDate }, { weekStartsOn: 0 });
  }, [startDate, endDate]);

  // Filter projects
  const filteredProjects = useMemo(() => {
    return projects.filter(p => {
      if (statusFilter !== 'all' && p.status !== statusFilter) return false;
      return true;
    });
  }, [projects, statusFilter]);

  const toggleProject = (projectId) => {
    setExpandedProjects(prev => {
      const newSet = new Set(prev);
      if (newSet.has(projectId)) {
        newSet.delete(projectId);
      } else {
        newSet.add(projectId);
      }
      return newSet;
    });
  };

  const navigateDate = (direction) => {
    if (direction === 'prev') {
      setCurrentDate(prev => subMonths(prev, viewMode === 'month' ? 1 : 3));
    } else {
      setCurrentDate(prev => addMonths(prev, viewMode === 'month' ? 1 : 3));
    }
  };

  const goToToday = () => {
    setCurrentDate(new Date());
  };

  const handleZoom = (direction) => {
    setDayWidth(prev => {
      if (direction === 'in') return Math.min(prev + 8, 60);
      return Math.max(prev - 8, 16);
    });
  };

  // Handle project click - navigate to project detail page
  const handleProjectClick = (project) => {
    navigate(`/projects/${project.id}`);
  };

  // Handle task click - open task detail modal
  const handleTaskClick = (task) => {
    setSelectedTask(task);
    setTaskModalOpen(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-4 animate-fade-in" data-testid="gantt-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Project Timeline</h1>
          <p className="text-slate-500 mt-1">Gantt view of projects and tasks</p>
        </div>
      </div>

      {/* Controls */}
      <Card className="mcc-card">
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center justify-between gap-4">
            {/* Date Navigation */}
            <div className="flex items-center gap-2">
              <Button variant="outline" size="icon" onClick={() => navigateDate('prev')}>
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button variant="outline" onClick={goToToday} className="px-4">
                <Calendar className="w-4 h-4 mr-2" />
                Today
              </Button>
              <Button variant="outline" size="icon" onClick={() => navigateDate('next')}>
                <ChevronRight className="w-4 h-4" />
              </Button>
              <span className="text-lg font-semibold text-slate-900 ml-2">
                {format(currentDate, 'MMMM yyyy')}
                {viewMode === 'quarter' && ` - ${format(addMonths(currentDate, 2), 'MMMM yyyy')}`}
              </span>
            </div>

            {/* View Controls */}
            <div className="flex items-center gap-3">
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="planning">Planning</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="on_hold">On Hold</SelectItem>
                  <SelectItem value="completed">Completed</SelectItem>
                </SelectContent>
              </Select>

              <Select value={viewMode} onValueChange={setViewMode}>
                <SelectTrigger className="w-[120px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="month">Month</SelectItem>
                  <SelectItem value="quarter">Quarter</SelectItem>
                </SelectContent>
              </Select>

              <div className="flex items-center border rounded-md">
                <Button variant="ghost" size="icon" onClick={() => handleZoom('out')} className="h-9 w-9">
                  <ZoomOut className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" onClick={() => handleZoom('in')} className="h-9 w-9">
                  <ZoomIn className="w-4 h-4" />
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Gantt Chart */}
      <Card className="mcc-card overflow-hidden">
        <div className="overflow-auto" ref={scrollContainerRef}>
          {/* Header */}
          <div className="flex border-b border-slate-200 bg-slate-50 sticky top-0 z-20">
            {/* Project Name Header */}
            <div className="w-64 min-w-64 flex-shrink-0 p-3 border-r border-slate-200 bg-slate-50 sticky left-0 z-30">
              <span className="font-semibold text-sm text-slate-700">Projects / Tasks</span>
            </div>
            
            {/* Timeline Header */}
            <div className="flex-1" style={{ minWidth: `${totalDays * dayWidth}px` }}>
              {/* Weeks Row */}
              <div className="flex border-b border-slate-200">
                {weeks.map((weekStart, i) => {
                  const weekEnd = endOfWeek(weekStart);
                  const daysInWeek = eachDayOfInterval({ 
                    start: weekStart < startDate ? startDate : weekStart,
                    end: weekEnd > endDate ? endDate : weekEnd 
                  }).length;
                  
                  return (
                    <div 
                      key={i}
                      className="text-center border-r border-slate-200 py-1"
                      style={{ width: `${daysInWeek * dayWidth}px` }}
                    >
                      <span className="text-xs font-medium text-slate-600">
                        {format(weekStart, 'MMM d')}
                      </span>
                    </div>
                  );
                })}
              </div>
              
              {/* Days Row */}
              <div className="flex">
                {days.map((day, i) => (
                  <div 
                    key={i}
                    className={`text-center border-r border-slate-100 py-1 ${
                      isToday(day) ? 'bg-blue-100' : !isSameMonth(day, currentDate) ? 'bg-slate-100/50' : ''
                    }`}
                    style={{ width: `${dayWidth}px`, minWidth: `${dayWidth}px` }}
                  >
                    <span className={`text-xs ${isToday(day) ? 'font-bold text-blue-700' : 'text-slate-500'}`}>
                      {format(day, 'd')}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Project Rows */}
          <div className="relative">
            {/* Today Line */}
            {isWithinInterval(new Date(), { start: startDate, end: endDate }) && (
              <div 
                className="absolute top-0 bottom-0 w-0.5 bg-red-500 z-10 pointer-events-none"
                style={{ 
                  left: `${256 + differenceInDays(new Date(), startDate) * dayWidth + dayWidth / 2}px`
                }}
              />
            )}

            {filteredProjects.length === 0 ? (
              <div className="p-12 text-center">
                <FolderKanban className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-slate-900 mb-2">No projects found</h3>
                <p className="text-slate-500">Try adjusting your filters or create a new project</p>
              </div>
            ) : (
              filteredProjects.map(project => (
                <ProjectRow
                  key={project.id}
                  project={project}
                  tasks={tasks}
                  startDate={startDate}
                  dayWidth={dayWidth}
                  totalDays={totalDays}
                  expanded={expandedProjects.has(project.id)}
                  onToggle={toggleProject}
                  onProjectClick={handleProjectClick}
                  onTaskClick={handleTaskClick}
                />
              ))
            )}
          </div>
        </div>
      </Card>

      {/* Legend */}
      <Card className="mcc-card">
        <CardContent className="py-3">
          <div className="flex flex-wrap items-center gap-6">
            <span className="text-sm font-medium text-slate-700">Legend:</span>
            <div className="flex flex-wrap items-center gap-4">
              {Object.entries(STATUS_COLORS).slice(0, 4).map(([status, colors]) => (
                <div key={status} className="flex items-center gap-2">
                  <div className="w-4 h-3 rounded" style={{ backgroundColor: colors.bar }} />
                  <span className="text-xs text-slate-600 capitalize">{status.replace('_', ' ')}</span>
                </div>
              ))}
              <div className="border-l pl-4 flex items-center gap-2">
                <div className="w-0.5 h-4 bg-red-500" />
                <span className="text-xs text-slate-600">Today</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Task Detail Modal */}
      <TaskDetailModal
        task={selectedTask}
        users={users}
        projects={projects}
        open={taskModalOpen}
        onClose={setTaskModalOpen}
      />
    </div>
  );
};
